/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { Component, ReactNode } from 'react';

import { FormattedMessage, injectIntl } from 'react-intl';
import _ from 'lodash';

import ZerpaTable, { ColumnObject } from '../layout/table/ZerpaTable';
import {
  Enrollment,
  ThirdPartyTrial,
  Trial,
  Invoice,
  QuipUser,
  Institute,
  Certificate,
  Submission,
  Review as ReviewType,
  ReviewRequest,
  SurveySubmissionMeta,
  DigitalSubmissionMeta,
  DigitalGroundTruthGroup,
} from '../../redux/types';
import { convertToQid, getEnrollmentTextTranslation } from '../../utilities/stringHelper';

type ExtendedTrial = Trial & {
  thirdPartyTrial?: ThirdPartyTrial;
}

type ExtendedEnrollment = Enrollment & {
  quipUser: QuipUser & {
    institute?: Institute;
  };
  certificate?: Certificate;
  invoices: Invoice[];
  submission?: Submission & {
    review: ReviewType & {
      reviewRequest: ReviewRequest;
    };
  };
  surveySubmissionMeta?: SurveySubmissionMeta;
  digitalSubmissionMeta?: DigitalSubmissionMeta[];
}

type Props = {
  enrollments: ExtendedEnrollment[];
  renderEnrollmentActions: (
    enrollment: ExtendedEnrollment, trial: ExtendedTrial,
  ) => React.ReactNode;
  trial: ExtendedTrial;
  intl: {
    formatMessage: (arg: Record<string, string>) => string;
  }
  digitalGroundTruthGroupById?: Record<number, DigitalGroundTruthGroup>;
}
class ParticipantList extends Component<Props> {
  static hasValidInvoice = (invoices: Invoice[]): boolean => (
    invoices ? _.some(invoices, (invoice) => !invoice?.cancelledAt) : false
  );

  render(): React.ReactNode {
    const {
      enrollments, trial, renderEnrollmentActions, intl, digitalGroundTruthGroupById,
    } = this.props;

    const { isDRT, version } = trial;
    const columsList: ColumnObject[] = [
      { text: 'Quip Id', keyName: 'userId', sortable: true },
      { text: <FormattedMessage id="trialdetails.title" />, keyName: 'title', sortable: true },
      { text: <FormattedMessage id="trialdetails.surname" />, keyName: 'surname', sortable: true },
      { text: <FormattedMessage id="trialdetails.firstname" />, keyName: 'firstname', sortable: true },
      { text: <FormattedMessage id="trialdetails.city" />, keyName: 'city', sortable: true },
      { text: <FormattedMessage id="trialdetails.attendeeInstitute" />, keyName: 'institute', sortable: true },
      {
        text: <FormattedMessage id="trialdetails.patientCare" />, keyName: 'isPatientCare', sortable: false, format: { align: 'center' },
      },
      { text: <FormattedMessage id="trials.enrollmentstatus" />, keyName: 'enrollmentStatus', sortable: false },
      {
        text: <FormattedMessage id="trialdetails.submissionStatus" />, keyName: 'submissionStatus', sortable: true,
      },
      {
        text: <FormattedMessage id="trialdetails.invoiced" />, keyName: 'isInvoiceCreated', sortable: false, format: { align: 'center' },
      },
      {
        text: <FormattedMessage id="trialdetails.enrollDate" />, keyName: 'enrollDate', sortable: true, isDate: true,
      },
    ];

    if (trial.thirdPartyTrial) {
      columsList.push({
        text: <FormattedMessage id="certificatesView.certificateIssued" />,
        keyName: 'certificateFile',
        sortable: true,
      });
    }

    if (trial.trialCategory === 5) {
      columsList.push({
        text: <FormattedMessage id="trials.enrollmentRole" />,
        keyName: 'enrollmentRole',
        sortable: true,
      });
    }

    // add columns for trial version 2
    if (trial.version === 2) {
      columsList.push({
        text: <FormattedMessage id="trialdetails.trialStatus" />,
        keyName: 'trialResult',
        sortable: true,
        format: { align: 'center' },
      });

      if (digitalGroundTruthGroupById
        && Object.keys(digitalGroundTruthGroupById).length > 1
        && enrollments.some((enrollment) => enrollment.digitalGroundTruthGroupId)) {
        columsList.splice(1, 0, {
          text: <FormattedMessage id="trialdetails.gtgroup" />,
          keyName: 'digitalGroundTruthGroup',
          sortable: true,
          format: { align: 'center' },
        });
      }
    }

    // old surveys will have groundTruthId directly, not in groups.
    if ((trial.version === null || trial.version === 1)
      && enrollments.some((enrollment) => enrollment.groundTruthId)) {
      columsList.splice(1, 0, {
        text: 'GT-id',
        keyName: 'groundTruthId',
        sortable: true,
        format: { align: 'center' },
      });
    }

    if (enrollments.some((enrollment) => enrollment.sampleset)) {
      columsList.splice(1, 0, {
        text: <FormattedMessage id="trialdetails.sampleset" />,
        keyName: 'sampleset',
        sortable: true,
        format: { align: 'center' },
      });
    }

    const getTrialResult = (surveySubmissionMeta?: SurveySubmissionMeta): ReactNode => {
      if (!surveySubmissionMeta || !surveySubmissionMeta.finalized) {
        return <FormattedMessage id="notFinalized" />;
      }

      return surveySubmissionMeta.passed ? intl.formatMessage({ id: 'pass' }) : intl.formatMessage({ id: 'fail' });
    };

    const getEnrollmentStatus = ({
      submission,
      digitalSubmissionMeta,
      surveySubmissionMeta,
    }: Pick<ExtendedEnrollment, 'surveySubmissionMeta' | 'submission' | 'digitalSubmissionMeta'>): string => {
      let started = false;
      let finalized = false;

      if (isDRT) {
        if (digitalSubmissionMeta?.length) {
          started = true;
          // need to check if user opened the second submission
          // while the first one is finalized - multiple anitbodies
          finalized = digitalSubmissionMeta.every((dsm) => dsm.submissionFinalized);
        }
      } else if (version && version >= 2) {
        if (digitalSubmissionMeta?.length) {
          started = true;
        }
        finalized = surveySubmissionMeta?.finalized || false;
      } else {
        if (submission) {
          started = true;
        }
        finalized = submission?.finalised || false;
      }

      if (!started) {
        return intl.formatMessage({ id: 'trialdetails.notstarted' });
      }

      return finalized
        ? intl.formatMessage({ id: 'trialdetails.submissionfinalizedTrue' })
        : intl.formatMessage({ id: 'trialdetails.submissionfinalizedFalse' });
    };

    const newEnrollments = enrollments.map((enrollment) => {
      const {
        submission,
        digitalSubmissionMeta = [],
        surveySubmissionMeta,
        digitalGroundTruthGroupId,
      } = enrollment;

      return {
        enrollmentId: enrollment.id,
        sampleset: enrollment.sampleset,
        surname: enrollment.quipUser.surname,
        userId: convertToQid(
          enrollment.quipUser.id,
          enrollment.quipUser.institute?.country || enrollment.quipUser.userCountry,
        ),
        title: enrollment.quipUser.title,
        firstname: enrollment.quipUser.firstname,
        city: enrollment.quipUser.institute?.city,
        institute: enrollment.quipUser.institute?.name,
        isPatientCare: <FormattedMessage id={enrollment.quipUser.institute?.isCompany ? 'no' : 'yes'} />,
        enrollmentStatus: <FormattedMessage id={getEnrollmentTextTranslation(enrollment.status)} />,
        isInvoiceCreated: <FormattedMessage id={ParticipantList.hasValidInvoice(enrollment.invoices) ? 'yes' : 'no'} />,
        enrollDate: enrollment.createdAt,
        certificateFile: <FormattedMessage id={enrollment.certificate && enrollment.certificate.file ? 'yes' : 'no'} />,
        submissionStatus:
          getEnrollmentStatus({ submission, surveySubmissionMeta, digitalSubmissionMeta }),
        enrollmentRole: enrollment.enrollmentRole || 'none',
        digitalGroundTruthGroup: digitalGroundTruthGroupId
          && digitalGroundTruthGroupById?.[digitalGroundTruthGroupId]?.index,
        groundTruthId: enrollment.groundTruthId,
        trialResult: getTrialResult(surveySubmissionMeta),
      };
    });

    return (
      <div style={{ display: 'block', overflow: 'auto' }}>
        <ZerpaTable
          dense
          columns={columsList}
          tableRows={newEnrollments}
          keyIdentifier="enrollmentId"
          renderOnSelected={(key) => {
            const selectedEnrollment = enrollments.find((enrollment) => enrollment.id === key);
            return selectedEnrollment && renderEnrollmentActions(selectedEnrollment, trial);
          }}
        />
      </div>
    );
  }
}

export default injectIntl(ParticipantList);
